/* global Word, console */
import { useState } from "react";
import { ReviewTileItem, DocumentTemplate, CreatedDocument } from "@/types/tile.types";

export interface UseTileDocumentCreationOptions {
  onSuccess?: (document: CreatedDocument) => void;
  onError?: (error: string) => void;
  onProgress?: (progress: { current: number; total: number; action: string }) => void;
  defaultTemplate?: DocumentTemplate;
}

export const useTileDocumentCreation = (options?: UseTileDocumentCreationOptions) => {
  const [isCreating, setIsCreating] = useState(false);
  const [creationProgress, setCreationProgress] = useState<{
    current: number;
    total: number;
    action: string;
  } | null>(null);

  const defaultTemplate: DocumentTemplate = {
    name: "Standard Review Report",
    header: "Legal Document Review Report",
    sections: [
      {
        title: "High Impact Issues",
        includeImpact: true,
        groupByImpact: true,
        format: "detailed",
      },
      {
        title: "Medium Impact Issues",
        includeImpact: true,
        groupByImpact: true,
        format: "list",
      },
      {
        title: "Low Impact Issues",
        includeImpact: true,
        groupByImpact: true,
        format: "list",
      },
    ],
    footer: "End of Review Report",
  };

  const updateProgress = (current: number, total: number, action: string) => {
    const progress = { current, total, action };
    setCreationProgress(progress);
    options?.onProgress?.(progress);
  };

  const formatTileContent = (
    tile: ReviewTileItem,
    format: "list" | "table" | "detailed"
  ): string => {
    const clause = tile.clause || "Unspecified Clause";
    const reason = tile.reason || tile.analysis || tile.explanation || "No details available";

    switch (format) {
      case "list":
        return `• **${clause}**: ${reason}`;

      case "table":
        return `| ${clause} | ${tile.impact || "N/A"} | ${reason} |`;

      case "detailed": {
        let detailed = `### ${clause}\n\n`;
        if (tile.impact && tile.impact !== "ALL") {
          detailed += `**Impact Level:** ${tile.impact}\n\n`;
        }
        if (tile.reason) {
          detailed += `**Issue:** ${tile.reason}\n\n`;
        }
        if (tile.analysis) {
          detailed += `**Analysis:** ${tile.analysis}\n\n`;
        }
        if (tile.explanation) {
          detailed += `**Explanation:** ${tile.explanation}\n\n`;
        }
        detailed += "---\n\n";
        return detailed;
      }

      default:
        return `${clause}: ${reason}`;
    }
  };

  const groupTilesByImpact = (tiles: ReviewTileItem[]): Record<string, ReviewTileItem[]> => {
    const groups: Record<string, ReviewTileItem[]> = {
      HIGH: [],
      MEDIUM: [],
      LOW: [],
      UNKNOWN: [],
    };

    tiles.forEach((tile) => {
      const impact = tile.impact?.toUpperCase() || "UNKNOWN";
      if (groups[impact]) {
        groups[impact].push(tile);
      } else {
        groups["UNKNOWN"] = groups["UNKNOWN"] || [];
        groups["UNKNOWN"].push(tile);
      }
    });

    return groups;
  };

  const createDocumentFromTemplate = async (
    tiles: ReviewTileItem[],
    template: DocumentTemplate = options?.defaultTemplate || defaultTemplate,
    documentTitle?: string
  ): Promise<CreatedDocument> => {
    setIsCreating(true);
    updateProgress(0, 100, "Starting document creation");

    try {
      let content = "";
      const totalSteps = template.sections.length + 3; // +3 for header, processing, footer
      let currentStep = 0;

      // Add header
      if (template.header) {
        content += `# ${template.header}\n\n`;
        content += `*Generated on: ${new Date().toLocaleDateString()}*\n\n`;
        content += `*Total Review Items: ${tiles.length}*\n\n`;
        content += "---\n\n";
      }

      currentStep++;
      updateProgress((currentStep / totalSteps) * 100, 100, "Adding header");

      // Group tiles by impact if needed
      const groupedTiles = groupTilesByImpact(tiles);

      currentStep++;
      updateProgress((currentStep / totalSteps) * 100, 100, "Grouping tiles");

      // Process each section
      for (const section of template.sections) {
        updateProgress((currentStep / totalSteps) * 100, 100, `Creating section: ${section.title}`);

        content += `## ${section.title}\n\n`;

        let sectionTiles: ReviewTileItem[] = [];

        if (section.groupByImpact) {
          // Extract tiles for specific impact level based on section title
          if (section.title.toLowerCase().includes("high")) {
            sectionTiles = groupedTiles["HIGH"] || [];
          } else if (section.title.toLowerCase().includes("medium")) {
            sectionTiles = groupedTiles["MEDIUM"] || [];
          } else if (section.title.toLowerCase().includes("low")) {
            sectionTiles = groupedTiles["LOW"] || [];
          } else {
            sectionTiles = tiles; // All tiles if no specific impact
          }
        } else {
          sectionTiles = tiles;
        }

        if (sectionTiles.length === 0) {
          content += "*No items found for this section.*\n\n";
        } else {
          // Add table header for table format
          if (section.format === "table") {
            content += "| Clause/Section | Impact | Description |\n";
            content += "|---|---|---|\n";
          }

          // Add tiles content
          sectionTiles.forEach((tile) => {
            content += formatTileContent(tile, section.format) + "\n";
            if (section.format === "detailed") {
              content += "\n";
            }
          });

          content += "\n";
        }

        currentStep++;
      }

      // Add summary section
      content += "## Summary\n\n";
      const impactCounts = {
        HIGH: groupedTiles["HIGH"]?.length || 0,
        MEDIUM: groupedTiles["MEDIUM"]?.length || 0,
        LOW: groupedTiles["LOW"]?.length || 0,
        UNKNOWN: groupedTiles["UNKNOWN"]?.length || 0,
      };

      content += "### Issue Breakdown by Impact:\n\n";
      Object.entries(impactCounts).forEach(([impact, count]) => {
        if (count > 0) {
          content += `- **${impact}**: ${count} items\n`;
        }
      });
      content += "\n";

      // Add footer
      if (template.footer) {
        content += "---\n\n";
        content += `*${template.footer}*\n`;
      }

      currentStep++;
      updateProgress((currentStep / totalSteps) * 100, 100, "Finalizing document");

      const createdDocument: CreatedDocument = {
        title: documentTitle || `${template.name} - ${new Date().toLocaleDateString()}`,
        content,
        wordCount: content.split(/\s+/).length,
        createdAt: new Date(),
      };

      updateProgress(100, 100, "Document created successfully");
      options?.onSuccess?.(createdDocument);

      return createdDocument;
    } catch (error: any) {
      console.error("Error creating document:", error);
      options?.onError?.(`Failed to create document: ${error.message}`);
      throw error;
    } finally {
      setIsCreating(false);
      setCreationProgress(null);
    }
  };

  const createNewWordDocument = async (
    tiles: ReviewTileItem[],
    template?: DocumentTemplate,
    title?: string
  ): Promise<boolean> => {
    try {
      const document = await createDocumentFromTemplate(tiles, template, title);

      // Create new Word document
      await Word.run(async (context) => {
        // Clear existing content
        context.document.body.clear();

        // Insert the generated content
        context.document.body.insertText(document.content, Word.InsertLocation.start);

        // Set document title if provided
        if (document.title) {
          const titleParagraph = context.document.body.paragraphs.getFirst();
          titleParagraph.styleBuiltIn = Word.BuiltInStyleName.title;
        }

        await context.sync();
      });

      return true;
    } catch (error: any) {
      console.error("Error creating Word document:", error);
      options?.onError?.(`Failed to create Word document: ${error.message}`);
      return false;
    }
  };

  const exportToMarkdown = async (
    tiles: ReviewTileItem[],
    template?: DocumentTemplate
  ): Promise<string> => {
    const document = await createDocumentFromTemplate(tiles, template);
    return document.content;
  };

  const exportToHTML = async (
    tiles: ReviewTileItem[],
    template?: DocumentTemplate
  ): Promise<string> => {
    const markdownContent = await exportToMarkdown(tiles, template);

    // Simple markdown to HTML conversion
    let htmlContent = markdownContent
      .replace(/^# (.+)$/gm, "<h1>$1</h1>")
      .replace(/^## (.+)$/gm, "<h2>$1</h2>")
      .replace(/^### (.+)$/gm, "<h3>$1</h3>")
      .replace(/^\* (.+)$/gm, "<li>$1</li>")
      .replace(/^• (.+)$/gm, "<li>$1</li>")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/^---$/gm, "<hr>")
      .replace(/\n\n/g, "</p><p>")
      .replace(/\n/g, "<br>");

    // Wrap in basic HTML structure
    htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Legal Review Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; }
          h1, h2, h3 { color: #333; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; }
          .impact-high { color: #d73527; font-weight: bold; }
          .impact-medium { color: #f57c00; font-weight: bold; }
          .impact-low { color: #388e3c; font-weight: bold; }
        </style>
      </head>
      <body>
        <p>${htmlContent}</p>
      </body>
      </html>
    `;

    return htmlContent;
  };

  const getAvailableTemplates = (): DocumentTemplate[] => {
    return [
      defaultTemplate,
      {
        name: "Executive Summary",
        header: "Executive Summary - Legal Review",
        sections: [
          {
            title: "Critical Issues Requiring Immediate Attention",
            groupByImpact: true,
            format: "detailed",
          },
          {
            title: "Summary of All Findings",
            format: "table",
          },
        ],
      },
      {
        name: "Detailed Analysis Report",
        header: "Comprehensive Legal Document Analysis",
        sections: [
          {
            title: "All Review Items",
            format: "detailed",
          },
        ],
        footer: "This report contains a detailed analysis of all identified issues.",
      },
      {
        name: "Action Items List",
        header: "Legal Review - Action Items",
        sections: [
          {
            title: "Items Requiring Action",
            format: "list",
          },
        ],
      },
    ];
  };

  return {
    isCreating,
    creationProgress,
    createDocumentFromTemplate,
    createNewWordDocument,
    exportToMarkdown,
    exportToHTML,
    getAvailableTemplates,
  };
};
